import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #dragState = {
    isDragging: false,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
    startTime: 0,
  };

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    
    // Desktop drag events
    this.refs.dialog.addEventListener('mousedown', this.handleDragStart);
    this.refs.dialog.addEventListener('mousemove', this.handleDragMove);
    this.refs.dialog.addEventListener('mouseup', this.handleDragEnd);
    this.refs.dialog.addEventListener('mouseleave', this.handleDragEnd);
    
    // Mobile touch events
    this.refs.dialog.addEventListener('touchstart', this.handleDragStart, { passive: true });
    this.refs.dialog.addEventListener('touchmove', this.handleDragMove, { passive: true });
    this.refs.dialog.addEventListener('touchend', this.handleDragEnd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('mousedown', this.handleDragStart);
    this.refs.dialog.removeEventListener('mousemove', this.handleDragMove);
    this.refs.dialog.removeEventListener('mouseup', this.handleDragEnd);
    this.refs.dialog.removeEventListener('mouseleave', this.handleDragEnd);
    this.refs.dialog.removeEventListener('touchstart', this.handleDragStart);
    this.refs.dialog.removeEventListener('touchmove', this.handleDragMove);
    this.refs.dialog.removeEventListener('touchend', this.handleDragEnd);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions || !sourceImage || !targetImage) return open();

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', transitionName);
    });

    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.#updateSelectionIndicators(activeIndex);

    this.#loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));

    window.globalFunction.check_zoom_slide_available();
  }, 50);

  /**
   * Updates the selected state on thumbnails and pagination indicators.
   * @param {number} index
   */
  #updateSelectionIndicators(index) {
    const { thumbnails, pagination } = this.refs;

    if (thumbnails) {
      Array.from(thumbnails.querySelectorAll('button')).forEach((button, buttonIndex) => {
        button.setAttribute('aria-selected', `${buttonIndex === index}`);
      });
    }

    if (pagination) {
      Array.from(pagination.querySelectorAll('button')).forEach((button, buttonIndex) => {
        button.setAttribute('aria-selected', `${buttonIndex === index}`);
      });
    }
  }

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const transitionName = `gallery-item`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.#loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.#updateSelectionIndicators(index);

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.#loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
    
    window.globalFunction.check_zoom_slide_available();
  }

  /**
   * Handles the start of a drag event (mouse or touch).
   * Works on both desktop and mobile.
   * @param {MouseEvent | TouchEvent} event - The drag start event.
   */
  handleDragStart = (event) => {
    // Only track drag if starting on the media container
    const target = event.target instanceof Element ? event.target.closest('.dialog-zoomed-gallery, .product-media-container, .product-media') : null;
    if (!target) return;

    this.#dragState.isDragging = true;
    this.#dragState.startTime = Date.now();
    
    if (event instanceof TouchEvent) {
      this.#dragState.startX = event.touches[0]?.clientX ?? 0;
      this.#dragState.startY = event.touches[0]?.clientY ?? 0;
    } else if (event instanceof MouseEvent) {
      this.#dragState.startX = event.clientX;
      this.#dragState.startY = event.clientY;
    }
  };

  /**
   * Handles the movement during a drag event.
   * @param {MouseEvent | TouchEvent} event - The drag move event.
   */
  handleDragMove = (event) => {
    if (!this.#dragState.isDragging) return;

    if (event instanceof TouchEvent) {
      this.#dragState.currentX = event.touches[0]?.clientX ?? 0;
      this.#dragState.currentY = event.touches[0]?.clientY ?? 0;
    } else if (event instanceof MouseEvent) {
      this.#dragState.currentX = event.clientX;
      this.#dragState.currentY = event.clientY;
    }
  };

  /**
   * Handles the end of a drag event and determines if a swipe occurred.
   * Triggers slide navigation based on swipe direction.
   * @param {MouseEvent | TouchEvent} event - The drag end event.
   */
  handleDragEnd = (event) => {
    if (!this.#dragState.isDragging) return;

    this.#dragState.isDragging = false;

    const deltaX = this.#dragState.currentX - this.#dragState.startX;
    const deltaY = this.#dragState.currentY - this.#dragState.startY;
    const threshold = 30; // Minimum pixels to consider as a swipe (reduced for mobile)
    const timeThreshold = 500; // Maximum milliseconds for a valid swipe
    const timeDelta = Date.now() - this.#dragState.startTime;

    // Only process horizontal swipes (ignore vertical scrolls)
    // Must be fast enough to be considered a swipe
    if (Math.abs(deltaX) > threshold && Math.abs(deltaX) > Math.abs(deltaY) && timeDelta < timeThreshold) {
      const { media } = this.refs;
      if (!media || media.length === 0) {
        this.#resetDragState();
        return;
      }

      // Determine slide direction
      const goToPrevious = deltaX > 0; // Swipe right
      const goToNext = deltaX < 0; // Swipe left

      // Find current visible slide
      getMostVisibleElement(media).then((mostVisibleElement) => {
        const currentIndex = media.indexOf(mostVisibleElement);
        let nextIndex = currentIndex;

        if (goToPrevious) {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : media.length - 1;
        } else if (goToNext) {
          nextIndex = currentIndex < media.length - 1 ? currentIndex + 1 : 0;
        }

        if (nextIndex !== currentIndex) {
          this.selectThumbnail(nextIndex, { behavior: 'smooth' });
        }
      });
    }

    this.#resetDragState();
  };

  /**
   * Reset drag state to initial values.
   * @private
   */
  #resetDragState() {
    this.#dragState = {
      isDragging: false,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      startTime: 0,
    };
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
